import { EventClickArg } from '@fullcalendar/core'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import DOMPurify from 'dompurify'
import { Lock, LockOpen } from 'lucide-react'
import React, { useEffect, useRef } from 'react'
import Tooltip from '../../../../components/common/Tooltip'
import { useCalendar } from '../../../../context/CalendarContext'
import EventDetail from './EventDetail'
import { useNavigate } from 'react-router'

interface CalendarContentProps {
    selectedDate: string | null
}

export interface EventType {
    businessID: number
    noteID: number
    title: string
    subtitle: string
    author: {
        name: string
        image: string
    }
    users: string
    createdAt: string
    type: string
    description: string
    status: number
}

interface eventInfoProps {
    event: {
        id: number
        title: string
        start: string
        end: string
        extendedProps: {
            businessID: number
            noteID: number
            subtitle: string
            author: {
                name: string
                image: string
            }
            users: string
            createdAt: string
            description: string
            status: number
        }
    }
}

const CalendarContent: React.FC<CalendarContentProps> = ({ selectedDate }) => {
    const navigate = useNavigate()
    const calendarRef = useRef<FullCalendar>(null)
    const { events } = useCalendar()

    //? Detail (drawer)
    const [open, setOpen] = React.useState(false)
    const [event, setEvent] = React.useState<EventType | null>(null)

    const handleEventClick = (clickInfo: EventClickArg) => {
        const { businessID } = clickInfo.event.extendedProps
        if (!businessID) return

        navigate(`/negocio/${businessID}`)
    }

    // Atualiza a visualização para o dia selecionado na sidebar
    useEffect(() => {
        if (selectedDate && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi()
            calendarApi.changeView('timeGridDay', selectedDate)
        }
    }, [selectedDate])

    if (!events) return null

    return (
        <div className='rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]'>
            <div className='custom-calendar overflow-y-hidden'>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    locale={ptBrLocale}
                    initialView='dayGridMonth'
                    headerToolbar={{
                        left: 'prev,next addEventButton',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    events={events}
                    selectable={true}
                    // select={handleDateSelect}
                    eventClick={handleEventClick}
                    eventContent={renderEventContent}
                    customButtons={{
                        addEventButton: {
                            text: 'Adicionar',
                            click: () => {
                                console.log('cria nova nota...')
                            }
                        }
                    }}
                />
            </div>
        </div>
    )
}

const renderEventContent = (eventInfo: eventInfoProps) => {
    const { subtitle, description, status } = eventInfo.event.extendedProps

    return (
        <Tooltip
            label={
                <div className='flex items-center gap-2'>
                    <p
                        className='text-xs'
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(description)
                        }}
                    />
                </div>
            }
        >
            <div
                className={`flex items-center justify-start px-3 py-4 rounded-2xl mb-1 relative cursor-pointer ${
                    status == 0
                        ? 'bg-warning-50 text-warning-500 dark:bg-warning-500/15 dark:text-warning-400'
                        : 'bg-brand-100 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-500'
                }`}
            >
                <div className='flex flex-col'>
                    {status == 0 ? <LockOpen className='w-4 h-4 mb-1' /> : <Lock className='w-4 h-4 mb-1' />}
                    <div className='fc-event-title text-xs font-semibold break-words whitespace-normal'>
                        {eventInfo.event.title}
                    </div>
                    <div className='break-words whitespace-normal'>{subtitle}</div>
                </div>
            </div>
        </Tooltip>
    )
}

export default CalendarContent
